import { RefObject, useEffect, useRef } from "react";

type Options = { threshold?: number; dwellMs?: number; enabled?: boolean };

/**
 * Fire `onSeen` once, when `ref` has been at least `threshold` visible for `dwellMs`.
 * Disconnects after firing and on unmount. No-ops when IntersectionObserver is missing.
 */
const useImpressionOnce = (
  ref: RefObject<Element | null>,
  onSeen: () => void,
  { threshold = 0.5, dwellMs = 1000, enabled = true }: Options = {},
) => {
  const fired = useRef(false);
  const callback = useRef(onSeen);
  callback.current = onSeen;

  useEffect(() => {
    const el = ref.current;
    if (!enabled || fired.current || !el || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
          if (!timer) {
            timer = setTimeout(() => {
              fired.current = true;
              callback.current();
              observer.disconnect();
            }, dwellMs);
          }
        } else if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold },
    );
    observer.observe(el);

    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [enabled, threshold, dwellMs]);
};

export default useImpressionOnce;
